/**
 * Viewer Component
 *
 * @author Jenny Lam <jlam@buildcentrix.com>
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @author Monika Gole <monika.gole@ncircletech.com>
 *
 * @copyright (c) 2023 Build Centrix
 */
import {
  Component,
  OnInit,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  Inject,
  ChangeDetectorRef,
} from '@angular/core';
import { HostListener } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { WdModelViewerService } from '../../../../services/modelViewer/wd-model-viewer.service';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { WdModelStatusService } from 'src/app/services/modelViewer/wd-model-status.service';
import { MatDialog } from '@angular/material/dialog';
import { ChangeItemStatusDialogComponent } from '../../../../components/modals/viewer-item-status/change-item-status-dialog.component';
import { ISpoolingAssemblyModel } from 'src/app/interfaces/spools/spool-interfaces';

@Component({
  selector: 'app-model-viewer-component',
  templateUrl: './viewer.component.html',
  styleUrls: ['./viewer.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewerComponent implements OnInit {
  // Inputs
  orderIndex: any = '';

  // Viewer variables
  accessToken = '';
  dataUrn = '';
  currentViewer: any = null;
  bcxExtensions: string[] = [];
  statusesMappingsData: any = [];
  modelItemStatusesData: any = [];

  // Subscriptions
  subscriptions = new Subscription();
  assemblyModel: ISpoolingAssemblyModel | null = null;

  // Viewer Height/Width
  screenHeight = 0;
  screenWidth = 0;

  // Viewer title and subtitle
  title = '';
  subtitle = '';

  // Booleans
  viewerLoading = true;

  /**
   * Constructor
   *
   * @since 4.56.0
   */
  constructor(
    private activatedRoute: ActivatedRoute,
    public wdModelViewerService: WdModelViewerService,
    public wdModelStatusService: WdModelStatusService,
    public matDialog: MatDialog,
    private translate: TranslateService,
    public changeDetectorRef: ChangeDetectorRef,
    @Inject('wdErrorService') public wdErrorService: any,
  ) {
    this.getScreenSize();
  }

  /**
   * Get/set screen width/height
   * Attached to a HostListener to listen for changes
   *
   * @since 4.56.0
   */
  @HostListener('window:resize', ['$event'])
  getScreenSize() {
    this.screenHeight = window.innerHeight - 155; // Window height minus offset for header
    this.screenWidth = window.innerWidth - 105; // Window width minus offset for sidebar
  }

  /**
   * Load viewer data
   *
   * @since 4.56.0
   */
  loadViewerData() {
    // Stop Loader
    this.viewerLoading = true;

    // Load Viewer Data
    this.wdModelViewerService
      .fetchViewerOrderData(this.orderIndex)
      .then(
        (response: any) => {
          const responseData = response.data;
          this.accessToken = responseData.accessToken;
          this.dataUrn = responseData.dataUrn;

          if (responseData.data) {
            // Set the required order info
            const { orderNumber } = responseData.orderInfo;
            this.title = [
              this.translate.instant('order.title.singular'),
              ' #' + orderNumber,
            ].join(' ');
            // Set the assembly model data if the data is in the response
            const { selectedIds, viewerState, elementIds } = responseData.data;
            this.assemblyModel = {
              selectedIds: selectedIds,
              viewerState: viewerState,
              elementIds: elementIds,
            };
            this.dataUrn = viewerState.seedURN;
            this.bcxExtensions = [
              'BCX.RevitStatusesPanelExtension',
              'BCX.ChangeItemStatusExtension',
            ];
          }
        },
        (error: any) => {
          // Error loading viewer data
          this.wdErrorService.userError(
            this.translate.instant('labels.error'),
            error.statusText,
          );
        },
      )
      .then(() => {
        // Get statuses mappings data
        return this.wdModelStatusService.fetchMappingStatusesData();
      })
      .then((response) => {
        this.statusesMappingsData = response.data;

        // Get model items statuses data
        return this.wdModelStatusService.fetchModelItemStatusesById(
          this.dataUrn,
        );
      })
      .then((response) => {
        this.modelItemStatusesData = response.data;
        // Stop Loader
        this.viewerLoading = false;
        // Begin Detecting UI Changes
        this.changeDetectorRef.detectChanges();
      })
      .catch((error: any) => {
        // Error loading statuses mappings data
        this.wdErrorService.userError(
          this.translate.instant('labels.error'),
          error.statusText,
        );
      });
  }

  /**
   * On Init
   *
   * @since 4.56.0
   */
  ngOnInit(): void {
    // Subscribe to Route Change
    this.subscriptions.add(
      this.activatedRoute.params.subscribe((params) => {
        this.orderIndex = params.orderIndex;
        // Only load viewer data when wdModelViewerService.environment is initialized and the orderIndex is fetched from the route
        this.loadViewerData();
      }),
    );

    // Set page translations
    this.setTranslations();
  }

  /**
   * Set page translations
   *
   * @since 4.56.0
   */
  setTranslations() {
    // After translations are loaded
    this.translate.get('order.title.singular').subscribe(() => {
      this.title = this.translate.instant('actions.loading');
      this.subtitle = this.translate.instant('modelViewer.title');
    });
  }

  /**
   * Get viewer from style guide to use in UI
   */
  handleItemLoaded(viewer: any): void {
    this.currentViewer = viewer;
  }

  /**
   * Handle update status toolbar button click
   */
  handleChangeItemStatus(data: any): void {
    if (data.dbIds.length > 0) {
      this.openChangeItemStatusDialog(data);
    } else {
      this.wdErrorService.userError(
        'No items selected',
        'Select items to update the status',
      );
    }
  }

  /**
   * Open change item status dialog
   */
  openChangeItemStatusDialog(data: any): void {
    data.statusesMappingsData = this.statusesMappingsData;
    data.selectedModelId = this.dataUrn;
    this.matDialog.open(ChangeItemStatusDialogComponent, {
      data,
      width: '600px',
    });
  }
}
